import { DrawerActions, useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../app/hooks';
import { MenuIcon } from '../components/MenuIcon';
import { ScreenLayout } from '../components/ScreenLayout';
import {
  DateTimePickerModal,
  SearchModal,
  SongHistoryContextualMenu,
  SongHistoryEmptyState,
  SongHistoryFilters,
  SongHistoryList,
} from '../components/SongHistory';
import { TabName } from '../components/TabName';
import { COLORS } from '../constants';
import { selectCurrentRadioSongHistoryConfig } from '../features/currentradio/store/selectors';
import { useAdjustedFontSize } from '../hooks/useAdjustedFontSize';
import { useFetchSongHistory } from '../hooks/useFetchSongHistory';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { SongHistoryEntry, SongHistoryTabModel } from '../models';
import { LeftDrawerNavigatorScreenProps } from '../navigation/LeftDrawerNavigator';

export default function SongHistoryScreen({
  route,
  navigation,
}: LeftDrawerNavigatorScreenProps<'SongHistory'>) {
  // controlled values for date filters
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [pickerMode, setPickerMode] = useState<'date' | 'time' | null>(null);
  const [showPickerModal, setShowPickerModal] = useState(false);

  // search state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // contextual menu state
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const [selectedSong, setSelectedSong] = useState<SongHistoryEntry | null>(null);

  // config
  const songHistoryConfig = useAppSelector(selectCurrentRadioSongHistoryConfig);
  const songLinksAvailable = songHistoryConfig?.songLinksAvailable ?? false;

  // song history hook
  const { songs, isLoading, error, isNotAvailable, fetchHistory } = useFetchSongHistory(
    date,
    time,
    searchTerm,
  );

  // screen specifi data
  const tabOptions = route.params?.options as SongHistoryTabModel;

  // dynamic styles
  const titleFontSize = useAdjustedFontSize(mvs(25));
  const titleLineHeight = useAdjustedFontSize(mvs(30));
  const { bodyHorizontalPadding } = useResponsiveLayout();

  const toggleLeftDrawer = useCallback(() => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  }, [navigation]);

  // reset filters when navigating to the screen
  useFocusEffect(
    useCallback(() => {
      setDate(null);
      setTime(null);
      setSearchTerm('');
    }, []),
  );

  // fetch song history on mount and when filters change
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const openPicker = useCallback((mode: 'date' | 'time') => {
    setPickerMode(mode);
    setShowPickerModal(true);
  }, []);

  const closePicker = useCallback(() => {
    setShowPickerModal(false);
    setPickerMode(null);
  }, []);

  const openSearch = useCallback(() => setShowSearchModal(true), []);
  const closeSearch = useCallback(() => setShowSearchModal(false), []);
  const onSaveSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      closeSearch();
    },
    [closeSearch],
  );

  const onSavePicker = useCallback(
    (selected: Date) => {
      if (pickerMode === 'date') {
        setDate(selected);
      } else if (pickerMode === 'time') {
        setTime(selected);
      }
      closePicker();
    },
    [pickerMode, closePicker],
  );

  const openContextualMenu = useCallback(() => setShowContextualMenu(true), []);
  const closeContextualMenu = useCallback(() => setShowContextualMenu(false), []);

  const handleSongPress = useCallback(
    (song: SongHistoryEntry) => {
      setSelectedSong(song);
      openContextualMenu();
    },
    [openContextualMenu],
  );

  const headerContent = useMemo(
    () => (
      <View style={styles.header}>
        <MenuIcon
          color={tabOptions.headerTextColor}
          toggleLeftDrawer={toggleLeftDrawer}
        />
        <TabName
          title={tabOptions.titre}
          color={tabOptions.headerTextColor}
          titleFontSize={titleFontSize}
          titleLineHeight={titleLineHeight}
        />
      </View>
    ),
    [
      tabOptions.headerTextColor,
      tabOptions.titre,
      toggleLeftDrawer,
      titleFontSize,
      titleLineHeight,
    ],
  );

  return (
    <ScreenLayout headerContents={headerContent}>
      <View style={[styles.container, { paddingHorizontal: bodyHorizontalPadding }]}>
        {/* date time filter inputs */}
        <SongHistoryFilters date={date} time={time} onOpenPicker={openPicker} />

        <View style={styles.listContainer}>
          {isLoading ? (
            // loading state
            <View style={styles.centerContent}>
              <ActivityIndicator size="large" color={COLORS.WHITE} />
            </View>
          ) : isNotAvailable ? (
            <SongHistoryEmptyState isNotAvailable />
          ) : error ? (
            <SongHistoryEmptyState isError onRetry={fetchHistory} />
          ) : // empty state
          songs.length === 0 ? (
            <SongHistoryEmptyState />
          ) : (
            // song history list
            <SongHistoryList
              songs={songs}
              onSongPress={songLinksAvailable ? handleSongPress : undefined}
            />
          )}
        </View>
      </View>

      {/* date time picker modal */}
      <DateTimePickerModal
        visible={showPickerModal}
        mode={pickerMode}
        initialValue={pickerMode === 'time' ? time : date}
        onCancel={closePicker}
        onSave={onSavePicker}
      />

      {/* search modal */}
      <SearchModal
        visible={showSearchModal}
        initialValue={searchTerm}
        onCancel={closeSearch}
        onSave={onSaveSearch}
      />

      {/* contextual menu for song actions */}
      <SongHistoryContextualMenu
        visible={showContextualMenu}
        onRequestClose={closeContextualMenu}
        song={selectedSong}
      />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  container: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
